import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Mail, Send } from 'lucide-react';
import { sendLotesVsCiclosReportEmail } from '../services/reportesV2.services';

// Diálogo para generar el PDF de conciliación del periodo y enviarlo por
// correo a los destinatarios configurados en `correosReporte` de la lavandería.
const SendReportDialog = ({ open, onOpenChange, correosReporteRaw, idLavanderia, startDate, endDate, laundryNombre }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [observacion, setObservacion] = useState('');

  const correos = useMemo(
    () => String(correosReporteRaw || '').split(',').map((s) => s.trim()).filter(Boolean),
    [correosReporteRaw],
  );

  const [selected, setSelected] = useState(() => new Set(correos));

  // Re-marcar todos por defecto cada vez que se abre el diálogo o cambia la lista de correos
  useEffect(() => {
    if (open) setSelected(new Set(correos));
  }, [open, correos]);

  const toggle = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const handleSend = async () => {
    const recipients = Array.from(selected);
    if (recipients.length === 0) return;

    setLoading(true);
    try {
      await sendLotesVsCiclosReportEmail({
        idLavanderia,
        startDate,
        endDate,
        recipients,
        observacion: observacion.trim() || undefined,
      });
      toast({
        title: 'Reporte enviado',
        description: `Se envió el reporte a ${recipients.length === 1 ? recipients[0] : `${recipients.length} destinatarios`}.`,
      });
      setObservacion('');
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Error al enviar el reporte',
        description: error?.message || 'No se pudo generar/enviar el reporte.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Enviar reporte por correo
          </DialogTitle>
          <DialogDescription>
            {laundryNombre ? `${laundryNombre} · ` : ''}
            {startDate?.slice(0, 10)} a {endDate?.slice(0, 10)}
          </DialogDescription>
        </DialogHeader>

        {correos.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            Esta lavandería no tiene correos de reporte configurados (<code>correosReporte</code>).
            Configúralos en Parametrización &gt; Centros de lavado.
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">Destinatarios</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {correos.map((email) => (
                  <div key={email} className="flex items-center gap-2">
                    <Checkbox
                      id={`recipient-${email}`}
                      checked={selected.has(email)}
                      onCheckedChange={() => toggle(email)}
                      disabled={loading}
                    />
                    <label htmlFor={`recipient-${email}`} className="text-sm cursor-pointer">
                      {email}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Observación adicional (opcional)
              </Label>
              <Textarea
                value={observacion}
                onChange={(e) => setObservacion(e.target.value)}
                placeholder="Agrega un comentario adicional para este reporte..."
                rows={3}
                disabled={loading}
              />
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={loading || correos.length === 0 || selected.size === 0}
            className="bg-[#0F3460] hover:bg-[#001F41] text-white"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendReportDialog;
